import { Entity, ThemeCompany, ThemeExposure, ThemeSchemaResponse } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { ThemeHeaderDetailed } from '@terminal/components/theme-header-detailed/ThemeHeaderDetailed';
import { useGraphRelations } from '@terminal/react-query-hooks/graph/useGraphRelations';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ThemePerformanceSection } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';

import { StepperSection } from '../components/stepper-section/StepperSection';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import styles from './ThemeOverviewTab.module.scss';

const TOP_COMPANIES_LIMIT = 10;

type ThemeOverviewTabBaseProps = {
  entity?: Entity;
  source: PosthogTrackingSource;
  title?: string;
  tooltipThemeId?: string;
};

type ThemeOverviewTabProps = ThemeOverviewTabBaseProps &
  (
    | {
        isPerfDrawer: true;
        theme: { theme: ThemeSchemaResponse };
      }
    | {
        isPerfDrawer?: false;
        theme: ThemeExposure | { theme: ThemeSchemaResponse };
      }
  );

export const ThemeOverviewTab = ({
  entity,
  theme,
  source,
  title,
  tooltipThemeId,
  isPerfDrawer,
}: ThemeOverviewTabProps) => {
  const themeType = 'type' in theme.theme ? theme.theme.type : undefined;
  const isMacroFinancial = themeType === 'macro' || themeType === 'financial';

  const {
    data: themeCompanies = [],
    isLoading: isThemeCompaniesLoading,
    error: themeCompaniesError,
  } = useThemeCompanies({
    themeId: theme.theme.id,
    enabled: !isMacroFinancial,
  });

  const {
    data: graphRelations,
    isLoading: isGraphRelationsLoading,
    error: graphRelationsError,
  } = useGraphRelations({
    node: theme.theme.id,
    nodeType: 'theme',
    relationKind: 'theme-company',
    direction: 'outbound',
    enabled: isMacroFinancial,
  });

  const macroFinancialCompanies: ThemeCompany[] =
    graphRelations?.edges
      .filter(
        edge =>
          edge.attributes.type === 'theme-company-financial' ||
          edge.attributes.type === 'theme-company-macro'
      )
      .flatMap(edge => {
        const company = edge.target.split('/')[1];
        if (!company) {
          return [];
        }

        return [
          {
            company,
            relationship: {
              id: edge.key,
              description: edge.attributes.description ?? '',
              rank: edge.attributes.rank,
              source_evidence: edge.attributes.source_evidence,
              evidence: edge.attributes.evidence,
            },
          },
        ];
      }) ?? [];

  const isLoading = isMacroFinancial
    ? isGraphRelationsLoading
    : isThemeCompaniesLoading;
  const error = isMacroFinancial ? graphRelationsError : themeCompaniesError;

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '700px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (error) {
    throw error;
  }

  const data = isMacroFinancial ? macroFinancialCompanies : themeCompanies;
  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  return (
    <div className={styles.root}>
      {isPerfDrawer ? (
        <ThemeHeaderDetailed theme={theme.theme} title={title} />
      ) : (
        <StepperSection
          entity={entity}
          theme={theme}
          title={title}
          activeIndex={1}
          description={theme.theme.description}
          rightHeaderNode={<FollowThemeButton themeId={theme.theme.id} />}
        />
      )}
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          themeId={theme.theme.id}
          entityTags={entityTags}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <TopCompaniesSection
          companies={slicedData}
          remainingCount={remainingCount}
          source={source}
          tooltipThemeId={tooltipThemeId}
        />
      )}
    </div>
  );
};
